import { useState } from 'react';
import { Link } from 'react-router-dom';

const STATES = {
  pending: {
    label: 'En attente',
    color: 'bg-warning-subtle text-warning border border-warning',
    icon: 'bi-clock-history',
    desc: "Le conducteur a envoyé une demande de réservation. Le propriétaire n'a pas encore répondu.",
    actor: 'Propriétaire',
    actions: ['Accepter → accepted', 'Refuser → rejected'],
    next: ['accepted', 'rejected'],
  },
  accepted: {
    label: 'Acceptée',
    color: 'bg-info-subtle text-info border border-info',
    icon: 'bi-check-circle',
    desc: 'Le propriétaire a accepté la demande. Le conducteur doit maintenant procéder au paiement.',
    actor: 'Conducteur',
    actions: ['Payer → paid', 'Ne pas payer (expiration) → rejected'],
    next: ['paid', 'rejected'],
  },
  rejected: {
    label: 'Refusée / Annulée',
    color: 'bg-danger-subtle text-danger border border-danger',
    icon: 'bi-x-circle',
    desc: 'La demande a été refusée par le propriétaire, ou annulée par le conducteur, ou le paiement a expiré.',
    actor: '—',
    actions: [],
    next: [],
  },
  paid: {
    label: 'Confirmée (payée)',
    color: 'bg-primary-subtle text-primary border border-primary',
    icon: 'bi-credit-card-2-front',
    desc: 'Le paiement a été capturé par Stripe. La location est confirmée. Les deux parties attendent le jour de la remise.',
    actor: 'Propriétaire',
    actions: ['Valider check-in → checkin_pending_validation'],
    next: ['checkin_pending_validation'],
  },
  checkin_pending_validation: {
    label: 'Check-in en attente',
    color: 'bg-warning-subtle text-warning border border-warning',
    icon: 'bi-clipboard-check',
    desc: "L'état des lieux de départ a été rempli par le conducteur. Le propriétaire doit le valider.",
    actor: 'Propriétaire',
    actions: ['Valider → in_progress'],
    next: ['in_progress'],
  },
  in_progress: {
    label: 'En cours',
    color: 'bg-success-subtle text-success border border-success',
    icon: 'bi-car-front-fill',
    desc: "Le véhicule est remis au conducteur. La location est active jusqu'à la date de retour.",
    actor: 'Conducteur + Propriétaire',
    actions: ['Soumettre checkout → (retour véhicule)', 'Signaler → dispute_open'],
    next: ['completed', 'dispute_open'],
  },
  completed: {
    label: 'Terminée',
    color: 'bg-success-subtle text-success border border-success',
    icon: 'bi-check2-all',
    desc: 'Le checkout a été validé par le propriétaire. Les km supplémentaires sont prélevés. Le virement est initié sous 24 h.',
    actor: '—',
    actions: [],
    next: [],
  },
  dispute_open: {
    label: 'Litige ouvert',
    color: 'bg-danger-subtle text-danger border border-danger',
    icon: 'bi-exclamation-triangle-fill',
    desc: "Un litige a été ouvert par le propriétaire ou le conducteur. Le dépôt de garantie est gelé jusqu'à résolution par l'équipe Moovicar.",
    actor: 'Équipe Moovicar',
    actions: ['Résoudre → completed ou rejected'],
    next: ['completed', 'rejected'],
  },
};

const STATE_ORDER = [
  'pending',
  'accepted',
  'paid',
  'checkin_pending_validation',
  'in_progress',
  'completed',
];

const SIDE_STATES = ['rejected', 'dispute_open'];

const Arrow = ({ label }) => (
  <div className="d-flex flex-column align-items-center my-1" style={{ minHeight: 36 }}>
    <div
      className="border-start border-2 border-secondary-subtle flex-grow-1"
      style={{ width: 2 }}
    ></div>
    <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: 11 }}>
      <i className="bi bi-arrow-down"></i>
      <span>{label}</span>
    </div>
  </div>
);

function StateCard({ stateKey, active, onClick }) {
  const s = STATES[stateKey];
  return (
    <button
      className={`btn text-start w-100 rounded-3 p-3 border-2 mb-1 ${active ? s.color + ' shadow-sm' : 'bg-white border'}`}
      onClick={() => onClick(stateKey)}
      style={{ transition: 'all .15s' }}
    >
      <div className="d-flex align-items-center gap-2">
        <i className={`bi ${s.icon}`} style={{ fontSize: 16 }}></i>
        <span className="fw-semibold" style={{ fontSize: 13 }}>
          {s.label}
        </span>
      </div>
    </button>
  );
}

export default function ReservationStatesPage() {
  const [selected, setSelected] = useState('pending');
  const s = STATES[selected];

  return (
    <div className="bg-body-tertiary min-vh-100 py-5">
      <div className="container" style={{ maxWidth: 900 }}>
        {/* Header */}
        <div className="text-center mb-5">
          <span
            className="badge bg-secondary-subtle text-secondary rounded-pill px-3 py-2 mb-3"
            style={{ fontSize: 13 }}
          >
            Documentation technique
          </span>
          <h1 className="fw-bold mb-2">États de réservation</h1>
          <p className="text-muted" style={{ maxWidth: 560, margin: '0 auto' }}>
            Diagramme complet des transitions de statuts d'une réservation Moovicar. Cliquez sur un
            état pour voir les détails.
          </p>
        </div>

        <div className="row g-4">
          {/* Colonne gauche — diagramme */}
          <div className="col-12 col-md-5">
            <div className="card border-0 shadow-sm rounded-4 p-3">
              <p
                className="text-muted small fw-semibold mb-3 text-uppercase"
                style={{ letterSpacing: 1, fontSize: 11 }}
              >
                Flux principal
              </p>

              {STATE_ORDER.map((key, i) => (
                <div key={key}>
                  <StateCard stateKey={key} active={selected === key} onClick={setSelected} />
                  {i < STATE_ORDER.length - 1 && (
                    <Arrow
                      label={
                        key === 'pending'
                          ? 'Accepté'
                          : key === 'accepted'
                            ? 'Paiement OK'
                            : key === 'paid'
                              ? 'Check-in rempli'
                              : key === 'checkin_pending_validation'
                                ? 'Validé'
                                : key === 'in_progress'
                                  ? 'Checkout validé'
                                  : ''
                      }
                    />
                  )}
                </div>
              ))}

              <hr className="my-3" />
              <p
                className="text-muted small fw-semibold mb-2 text-uppercase"
                style={{ letterSpacing: 1, fontSize: 11 }}
              >
                États terminaux / exception
              </p>
              {SIDE_STATES.map((key) => (
                <StateCard
                  key={key}
                  stateKey={key}
                  active={selected === key}
                  onClick={setSelected}
                />
              ))}
            </div>
          </div>

          {/* Colonne droite — détails */}
          <div className="col-12 col-md-7">
            <div className="card border-0 shadow-sm rounded-4 p-4 h-100">
              {/* Badge statut */}
              <span
                className={`badge rounded-pill mb-3 px-3 py-2 ${s.color}`}
                style={{ fontSize: 13, alignSelf: 'flex-start' }}
              >
                <i className={`bi ${s.icon} me-2`}></i>
                {s.label}
              </span>

              <p className="text-body mb-4" style={{ fontSize: 14, lineHeight: 1.7 }}>
                {s.desc}
              </p>

              {/* Qui agit */}
              <div className="mb-3">
                <p
                  className="text-muted small fw-semibold mb-2 text-uppercase"
                  style={{ letterSpacing: 1, fontSize: 11 }}
                >
                  Acteur responsable
                </p>
                <span
                  className="badge bg-body-secondary text-body rounded-pill px-3 py-2"
                  style={{ fontSize: 13 }}
                >
                  <i className="bi bi-person me-1"></i>
                  {s.actor}
                </span>
              </div>

              {/* Transitions */}
              {s.actions.length > 0 && (
                <div className="mb-3">
                  <p
                    className="text-muted small fw-semibold mb-2 text-uppercase"
                    style={{ letterSpacing: 1, fontSize: 11 }}
                  >
                    Transitions possibles
                  </p>
                  <ul className="mb-0 ps-0 list-unstyled">
                    {s.actions.map((a) => (
                      <li
                        key={a}
                        className="d-flex align-items-center gap-2 mb-2"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-arrow-right-circle-fill text-primary flex-shrink-0"></i>
                        <code className="text-body">{a}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Passe vers */}
              {s.next.length > 0 && (
                <div>
                  <p
                    className="text-muted small fw-semibold mb-2 text-uppercase"
                    style={{ letterSpacing: 1, fontSize: 11 }}
                  >
                    Prochains états possibles
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    {s.next.map((key) => (
                      <button
                        key={key}
                        className={`btn btn-sm rounded-pill border-2 ${STATES[key].color}`}
                        style={{ fontSize: 12 }}
                        onClick={() => setSelected(key)}
                      >
                        <i className={`bi ${STATES[key].icon} me-1`}></i>
                        {STATES[key].label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {s.next.length === 0 && s.actions.length === 0 && (
                <div className="alert alert-secondary d-flex gap-2 mb-0" style={{ fontSize: 13 }}>
                  <i className="bi bi-flag-fill flex-shrink-0 mt-1"></i>
                  <span>État terminal — aucune transition automatique possible.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Légende couleurs */}
        <div className="card border-0 shadow-sm rounded-4 p-4 mt-4">
          <p
            className="text-muted small fw-semibold mb-3 text-uppercase"
            style={{ letterSpacing: 1, fontSize: 11 }}
          >
            Légende
          </p>
          <div className="d-flex flex-wrap gap-2">
            {[
              {
                color: 'bg-warning-subtle text-warning border border-warning',
                label: 'Action en attente',
              },
              { color: 'bg-info-subtle text-info border border-info', label: 'Information' },
              { color: 'bg-primary-subtle text-primary border border-primary', label: 'Paiement' },
              {
                color: 'bg-success-subtle text-success border border-success',
                label: 'Actif / Terminé',
              },
              {
                color: 'bg-danger-subtle text-danger border border-danger',
                label: 'Erreur / Annulation',
              },
            ].map(({ color, label }) => (
              <span
                key={label}
                className={`badge rounded-pill px-3 py-2 ${color}`}
                style={{ fontSize: 12 }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mt-4">
          <Link to="/regles-business" className="btn btn-outline-primary rounded-3 btn-sm">
            <i className="bi bi-file-text me-1"></i>Règles business
          </Link>
          <Link to="/comment-ca-marche" className="btn btn-outline-secondary rounded-3 btn-sm">
            <i className="bi bi-map me-1"></i>User flows
          </Link>
        </div>
      </div>
    </div>
  );
}
